var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable */
import { useApolloClient } from '@apollo/client';
import moment from 'moment';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ComponentBadge from '../ComponentBadge/ComponentBadge';
import styles from './OrderManagerItem.module.scss';
import GET_MANUFACTURER_PRODUCTION_CALENDAR_DAYS from 'api/queries/GET_MANUFACTURER_PRODUCTION_CALENDAR_DAYS';
import registerOrderSpecificationAllocationIncompleteErrorMatcher from 'errors/orderSpecificationAllocationIncompleteError';
import CalendarIcon from 'images/v5/icons/calendar.svg';
import UserIcon from 'images/v5/icons/user-01.svg';
import { useDispatch } from 'react-redux';
import { openModal } from 'store/layoutSlice';
import { setOrderSpecificationProductionStartDate } from 'store/orderProductionManagerSlice';
import { addToast } from 'store/toastsSlice';
import env from 'utils/env';
import toUtcDateOnlyISOString from 'utils/toUtcDateOnlyISOString';
import Avatar from '../Avatar/Avatar';
import Button from '../Button/Button';
import CalendarRange from '../CalendarRange/CalendarRange';
import Dropdown from '../Dropdown/Dropdown';
import StatusBadge from '../StatusBadge';
function OrderManagerItem({ specificationData, orderId }) {
    var _a, _b, _c;
    const { t } = useTranslation(['specification', 'orders']);
    const dispatch = useDispatch();
    const apolloClient = useApolloClient();
    registerOrderSpecificationAllocationIncompleteErrorMatcher();
    const { specification, modelsCount, suppliersCount, priority, manufacturer } = specificationData;
    const isProductionApproved = specificationData.productionApproved === true;
    const modelQuantity = (_b = (_a = specificationData.sumOfModelQuantities) !== null && _a !== void 0 ? _a : modelsCount) !== null && _b !== void 0 ? _b : 0;
    const manufacturerAvatarSrc = (manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.picture)
        ? `${env.static}/${manufacturer.picture}`
        : null;
    const productionDate = specificationData.productionStartDate
        ? moment(specificationData.productionStartDate)
        : null;
    const [occupiedOrderIdsByDate, setOccupiedOrderIdsByDate] = useState({});
    const [occupiedOrdersByDate, setOccupiedOrdersByDate] = useState({});
    const latestCalendarRequestRef = useRef('');
    const latestCollectedDate = specificationData.latestCollectedDate
        ? moment(specificationData.latestCollectedDate).startOf('day')
        : null;
    const disabledDates = useMemo(() => {
        var _a;
        if (!((_a = manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.nonWorkingDays) === null || _a === void 0 ? void 0 : _a.length)) {
            return [];
        }
        const dates = [];
        manufacturer.nonWorkingDays.forEach((range) => {
            const start = moment(range.startDate).startOf('day');
            const end = moment(range.endDate).startOf('day');
            const cursor = start.clone();
            while (cursor.isSameOrBefore(end, 'day')) {
                dates.push(cursor.clone());
                cursor.add(1, 'day');
            }
        });
        return dates;
    }, [manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.nonWorkingDays]);
    const occupiedDates = useMemo(() => {
        var _a;
        if (!((_a = specificationData.manufacturerOccupiedDates) === null || _a === void 0 ? void 0 : _a.length)) {
            return [];
        }
        return specificationData.manufacturerOccupiedDates.map((date) => moment(date).startOf('day'));
    }, [specificationData.manufacturerOccupiedDates]);
    const priorityLabel = {
        HIGH: t('orders:PRIORITY_HIGH'),
        NEUTRAL: t('orders:PRIORITY_NEUTRAL'),
        LOW: t('orders:PRIORITY_LOW'),
    }[priority || 'LOW'];
    const priorityColor = {
        HIGH: 'positive',
        NEUTRAL: 'neutral',
        LOW: 'negative',
    }[priority || 'LOW'];
    const manufacturerName = (manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.shortName) || (manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.fullName) || t('orders:ASSIGN_MANUFACTURER');
    const manufacturerLabel = (manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.id)
        ? `${manufacturerName} (${(_c = manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.employeesCount) !== null && _c !== void 0 ? _c : 0})`
        : manufacturerName;
    const handleProductionDateChange = (close) => (startDate) => {
        dispatch(setOrderSpecificationProductionStartDate({
            orderId,
            specificationId: specification.id,
            productionStartDate: toUtcDateOnlyISOString(startDate),
        }))
            .unwrap()
            .then(() => {
            close();
        })
            .catch((error) => {
            if ((error === null || error === void 0 ? void 0 : error.exceptionCode) === 'order_specification_allocation_incomplete') {
                dispatch(addToast({
                    theme: 'ghost',
                    color: 'notice',
                    title: t('orders:PRODUCTION_START_DATE_ALLOCATION_INCOMPLETE_TITLE'),
                    description: t('orders:PRODUCTION_START_DATE_ALLOCATION_INCOMPLETE_DESCRIPTION'),
                }));
                return;
            }
            dispatch(addToast({
                theme: 'ghost',
                color: 'negative',
                title: t('orders:PRODUCTION_START_DATE_BLOCKED_TITLE'),
                description: t('orders:PRODUCTION_START_DATE_BLOCKED_FETCH_ERROR'),
            }));
        });
    };
    const handleCalendarVisibleRangeChange = useCallback((visibleStartDate, visibleEndDate) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (!(manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.id)) {
            setOccupiedOrderIdsByDate({});
            setOccupiedOrdersByDate({});
            return;
        }
        const startDate = toUtcDateOnlyISOString(visibleStartDate);
        const endDate = toUtcDateOnlyISOString(visibleEndDate);
        if (!startDate || !endDate) {
            setOccupiedOrderIdsByDate({});
            setOccupiedOrdersByDate({});
            return;
        }
        const requestKey = `${manufacturer.id}:${startDate}:${endDate}`;
        latestCalendarRequestRef.current = requestKey;
        try {
            const response = yield apolloClient.query({
                query: GET_MANUFACTURER_PRODUCTION_CALENDAR_DAYS,
                variables: {
                    manufacturerId: manufacturer.id,
                    startDate,
                    endDate,
                },
                fetchPolicy: 'network-only',
            });
            if (latestCalendarRequestRef.current !== requestKey) {
                return;
            }
            const calendarDays = ((_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.node) === null || _b === void 0 ? void 0 : _b.productionCalendarDays) || [];
            const occupiedOrderIdsByDateMap = Object.fromEntries(calendarDays.map((day) => [
                moment(day.date).format('YYYY-MM-DD'),
                day.orderIds || [],
            ]));
            const occupiedOrdersByDateMap = Object.fromEntries(calendarDays.map((day) => [
                moment(day.date).format('YYYY-MM-DD'),
                (day.orders || []).map((order) => ({
                    orderId: order === null || order === void 0 ? void 0 : order.orderId,
                    specificationNames: (order === null || order === void 0 ? void 0 : order.specificationNames) || [],
                })),
            ]));
            setOccupiedOrderIdsByDate(occupiedOrderIdsByDateMap);
            setOccupiedOrdersByDate(occupiedOrdersByDateMap);
        }
        catch (error) {
            if (latestCalendarRequestRef.current === requestKey) {
                setOccupiedOrderIdsByDate({});
                setOccupiedOrdersByDate({});
            }
        }
    }), [apolloClient, manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.id]);
    const allocationsBlocked = !isProductionApproved &&
        (!(manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.id) || !productionDate || specificationData.allocationComplete === false);
    return (React.createElement("div", { className: styles.wrap },
        React.createElement("div", { className: styles.headerWrap },
            React.createElement("div", null,
                React.createElement(ComponentBadge, { name: specification.itemCode, category: specification.fancyName || specification.description || '', missingData: allocationsBlocked, onClick: () => dispatch(openModal({
                        name: 'specificationProductionManager',
                        data: {
                            specificationId: specification.id,
                            productionApproved: isProductionApproved,
                        },
                    })) })),
            React.createElement("div", null,
                React.createElement("p", null,
                    modelQuantity,
                    " (",
                    t('model', { count: modelQuantity }),
                    ")")),
            React.createElement("div", null,
                React.createElement("p", { className: styles.pill },
                    React.createElement(UserIcon, null),
                    React.createElement("span", null,
                        suppliersCount,
                        " ",
                        t('supplier', { count: suppliersCount })))),
            React.createElement("div", null, (manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.id) && !isProductionApproved ? (React.createElement(Dropdown, { closeOnClick: false, position: "bottomLeft", trigger: React.createElement("p", { className: styles.pill },
                    React.createElement(CalendarIcon, null),
                    React.createElement("span", null, productionDate ? productionDate.format('DD.MM.YYYY') : '-')) }, (close) => (React.createElement(CalendarRange, { startDate: productionDate, onDateChange: handleProductionDateChange(close), onVisibleRangeChange: handleCalendarVisibleRangeChange, singleDate: true, minDate: latestCollectedDate || moment().startOf('day'), disabledDates: disabledDates, occupiedDates: occupiedDates, occupiedDateOrderIds: occupiedOrderIdsByDate, occupiedDateOrders: occupiedOrdersByDate, legend: {
                    nonWorking: t('orders:planning_calendar_non_working_legend'),
                    occupied: t('orders:planning_calendar_occupied_legend'),
                } })))) : (React.createElement("p", { className: `${styles.pill} ${styles.pillDisabled}` },
                React.createElement(CalendarIcon, null),
                React.createElement("span", null, productionDate ? productionDate.format('DD.MM.YYYY') : '-')))),
            React.createElement("div", null,
                React.createElement(StatusBadge, { noIcon: true, size: "m", theme: "fill", color: priorityColor, onClick: isProductionApproved
                        ? undefined
                        : () => dispatch(openModal({
                            name: 'setPriority',
                            data: { specificationId: specification.id },
                        })) }, priorityLabel)),
            React.createElement("div", null,
                React.createElement(Button, { theme: "ghost", leftIcon: React.createElement(Avatar, { size: "s", src: manufacturerAvatarSrc, userName: manufacturerName }), disabled: isProductionApproved, onClick: isProductionApproved
                        ? undefined
                        : () => dispatch(openModal({
                            name: 'assignManufacturer',
                            data: { specificationId: specification.id },
                        })) }, manufacturerLabel)),
            !isProductionApproved && (React.createElement("div", null,
                React.createElement(Dropdown, null,
                    React.createElement(Button, { align: "left", theme: "ghost", onClick: () => dispatch(openModal({
                            name: 'setPriority',
                            data: { specificationId: specification.id },
                        })) }, t('orders:SET_PRIORITY')),
                    React.createElement(Button, { align: "left", theme: "ghost", onClick: () => dispatch(openModal({
                            name: 'assignManufacturer',
                            data: { specificationId: specification.id },
                        })) }, t('orders:ASSIGN_MANUFACTURER'))))))));
}
export default OrderManagerItem;
